import type { CSSProperties, ReactNode } from 'react';

export type EvidenceTapeProps = {
  left?: number | string;
  right?: number | string;
  top?: number | string;
  bottom?: number | string;
  /** Tape height in px. Default 56. */
  height?: number;
  /** Rotation degrees. Default -3 (mild diagonal). */
  rot?: number;
  /** Optional centred label. Renders inside `.evidence-tape-label`. */
  label?: ReactNode;
  /** Override the label rotation (relative to the tape rot). */
  labelRot?: number;
  /** Inline label font-size override (default 22). Use small (e.g. 11) for slim ribbons. */
  labelSize?: number;
  /** Inline label height override (default 30). */
  labelHeight?: number;
  className?: string;
  style?: CSSProperties;
};

/**
 * Yellow + black diagonal "evidence" caution-tape ribbon. Drop in via
 * absolute positioning relative to a positioned ancestor — typically a
 * `.dossier-board` stage or a paper sheet. Keep ribbon labels short
 * (NE PAS REGARDER, BRIEFING ROOM) — they read at a glance.
 */
export const EvidenceTape = ({
  left,
  right,
  top,
  bottom,
  height = 56,
  rot = -3,
  label,
  labelRot = 0,
  labelSize = 22,
  labelHeight = 30,
  className,
  style,
}: EvidenceTapeProps) => {
  const wrapStyle: CSSProperties = {
    transform: `rotate(${rot}deg)`,
    transformOrigin: 'center',
    height,
    ...(style ?? {}),
  };
  if (left !== undefined) wrapStyle.left = left;
  if (right !== undefined) wrapStyle.right = right;
  if (top !== undefined) wrapStyle.top = top;
  if (bottom !== undefined) wrapStyle.bottom = bottom;

  return (
    <div className={['evidence-tape', className ?? ''].filter(Boolean).join(' ')} style={wrapStyle}>
      {label !== null && (
        <div
          className="evidence-tape-label"
          style={{
            transform: `rotate(${labelRot}deg)`,
            fontSize: labelSize,
            height: labelHeight,
            lineHeight: `${labelHeight}px`,
            padding: '0 10px',
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
};
